/**
 * Wire format for carrying a CIO filter set on a URL (SFCC-11958).
 *
 * The PLP loader builds the grid's filters once (`baseCioFilters`); an in-grid
 * Builder carousel has to browse under that same set, which means round-tripping
 * it through the `/resources/cio-browse` query string. These two functions are
 * inverses and are the ONLY definition of that encoding — the `filters[` prefix
 * and the key offset live here, not at the call sites, because a divergence
 * between them fails silently: the carousel just browses unfiltered, which is
 * the exact defect this ticket fixes.
 *
 * The shape mirrors CIO's own request form (`applyListingParams` emits the same
 * `filters[key]=value` for the real browse call), so the URL stays readable in
 * logs and as a cache key.
 */

const PREFIX = "filters[";
const SUFFIX = "]";

/** CIO filter set → URL params. `null` / empty yields no params. */
export function toFilterParams(
  filters: Record<string, string[]> | null | undefined,
): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, values] of Object.entries(filters ?? {})) {
    for (const value of values)
      params.append(`${PREFIX}${key}${SUFFIX}`, value);
  }
  return params;
}

/**
 * URL params → CIO filter set. Ignores every non-`filters[...]` param, so it is
 * safe to run over a whole request URL (which also carries `categoryId`,
 * `resultsPerPage`, and whatever else the fetcher appended).
 */
export function fromFilterParams(
  params: URLSearchParams,
): Record<string, string[]> {
  const filters: Record<string, string[]> = {};
  for (const [key, value] of params) {
    if (!key.startsWith(PREFIX) || !key.endsWith(SUFFIX)) continue;
    const name = key.slice(PREFIX.length, -SUFFIX.length);
    if (!name) continue;
    (filters[name] ??= []).push(value);
  }
  return filters;
}
